"use client";

import { useCallback, useLayoutEffect, useRef, useState } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/flowbite/tooltip";
import { cn } from "@/shared/helpers/cn.helper";

interface TruncatedTextWithTooltipProps {
  text: string;
  className?: string;
  tooltipClassName?: string;
}

export function TruncatedTextWithTooltip({
  text,
  className,
  tooltipClassName,
}: TruncatedTextWithTooltipProps) {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const measureTruncation = useCallback(() => {
    const element = textRef.current;
    if (element == null) {
      return;
    }

    const nextIsTruncated = element.scrollWidth > element.clientWidth + 1;
    setIsTruncated(nextIsTruncated);
    if (!nextIsTruncated) {
      setIsOpen(false);
    }
  }, []);

  useLayoutEffect(() => {
    measureTruncation();
  }, [measureTruncation, text]);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (element == null || typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(measureTruncation);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [measureTruncation]);

  return (
    <Tooltip
      open={isTruncated && isOpen}
      onOpenChange={(nextOpen) => {
        if (isTruncated) {
          setIsOpen(nextOpen);
        }
      }}
    >
      <TooltipTrigger asChild>
        <span
          ref={textRef}
          className={cn("block truncate", className)}
        >
          {text}
        </span>
      </TooltipTrigger>
      <TooltipContent
        className={cn("max-w-[360px] whitespace-normal", tooltipClassName)}
        showArrow={false}
      >
        {text}
      </TooltipContent>
    </Tooltip>
  );
}
